/**
 * The five steps above the remittance sheet: where, what, and how much.
 *
 *   1  Country   where the business is, and therefore its currency
 *   2  State     which the default fee can be priced against
 *   3  Vehicle   the category the sheet below is about
 *   4  Fee       what that category owes, from a date
 *   5  Drivers   the sheet itself, which is the page's own job
 *
 * Steps 1 and 2 are the business's own record, not a filter: changing the
 * state changes what every default is priced against, so it saves rather than
 * narrows. Step 3 IS a filter — it decides which drivers the sheet shows and
 * which category step 4 is editing.
 *
 * A fee is never edited in place. Saving one closes the standing rate the day
 * before the new one starts and inserts a new row, so a week already accrued
 * keeps the figure it charged. The history panel is there to show that.
 */

import { useState, type ReactNode } from 'react';
import { Bike, Bus, Car, History, Pencil, Truck, Check } from 'lucide-react';
import {
  REMITTANCE_FREQUENCIES,
  REMITTANCE_FREQUENCY_LABELS,
  VEHICLE_CATEGORIES,
  VEHICLE_CATEGORY_LABELS,
  findCountry,
  findState,
  statesOf,
  type RemittanceFrequency,
  type VehicleCategory,
  type VehicleRateView,
} from '@cenox/shared';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { Badge, Button, Card, Input, Modal, Select, cn } from '../../components/ui';
import { useBusinessRecord, useSetBusinessState, useSetVehicleRate, useVehicleRates } from './data';

/**
 * An icon per category.
 *
 * Lucide has no keke or van, so a tricycle borrows the bike and a van borrows
 * the truck. Better a familiar shape than a wrong one: the label underneath is
 * what names the thing.
 */
const CATEGORY_ICONS: Record<VehicleCategory, typeof Bike> = {
  motorcycle: Bike,
  car: Car,
  van: Truck,
  truck: Truck,
  bus: Bus,
  tricycle: Bike,
};

function StepHeading({ step, title, hint }: { step: number; title: string; hint?: string }): ReactNode {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700">
          {step}
        </span>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {hint ? <p className="mt-1 pl-7 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export function RemittanceConfig({
  category,
  onCategoryChange,
}: {
  category: VehicleCategory;
  onCategoryChange: (next: VehicleCategory) => void;
}): ReactNode {
  const { business, can } = useAuth();
  const mayManage = can('remittance:manage');

  const countryCode = business?.countryCode ?? 'NG';
  const country = findCountry(countryCode);
  const states = statesOf(countryCode);

  const record = useBusinessRecord();
  const setState = useSetBusinessState();
  const rates = useVehicleRates();
  const [editingFee, setEditingFee] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const standing = (rates.data ?? []).filter((r) => r.effectiveTo === null);
  const current = standing.find((r) => r.category === category);
  const currentState = record.data?.stateCode ?? null;

  return (
    <div className="mb-5 grid gap-4 lg:grid-cols-2">
      {/* Steps 1 and 2 — where the business is. */}
      <Card>
        <StepHeading
          step={1}
          title="Country"
          hint={
            country
              ? `${country.name} · amounts in ${country.currencyCode}`
              : 'Set in Settings → Business'
          }
        />
        <p className="pl-7 text-sm text-slate-700">
          {country?.name ?? countryCode}{' '}
          <span className="text-slate-400">({business?.currencyCode ?? country?.currencyCode})</span>
        </p>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <StepHeading
            step={2}
            title="State"
            hint={
              states.length === 0
                ? 'No states are carried for this country, so fees apply country-wide.'
                : 'Where this fleet operates. A default can be priced per state.'
            }
          />
          {states.length === 0 ? (
            <p className="pl-7 text-sm text-slate-500">—</p>
          ) : mayManage ? (
            <div className="pl-7">
              <Select
                aria-label="State"
                value={currentState ?? ''}
                onChange={(event) => setState.mutate(event.target.value || null)}
                placeholder="Choose a state"
                options={states.map((s) => ({ value: s.code, label: s.name }))}
                disabled={setState.isPending}
              />
              {setState.isError ? (
                <p role="alert" className="mt-1.5 text-xs text-red-700">
                  {setState.error instanceof ApiError
                    ? setState.error.message
                    : 'Could not save the state.'}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="pl-7 text-sm text-slate-700">
              {findState(countryCode, currentState)?.name ?? 'Not set'}
            </p>
          )}
        </div>
      </Card>

      {/* Steps 3 and 4 — what, and how much. */}
      <Card>
        <StepHeading
          step={3}
          title="Vehicle type"
          hint="The kind of vehicle whose default fee is set below. Filter the riders with Vehicle type under the cards."
        />
        <div className="grid grid-cols-3 gap-2 pl-7 sm:grid-cols-6 lg:grid-cols-3 xl:grid-cols-6">
          {VEHICLE_CATEGORIES.map((value) => {
            const Icon = CATEGORY_ICONS[value];
            const selected = value === category;
            return (
              <button
                key={value}
                type="button"
                onClick={() => onCategoryChange(value)}
                aria-pressed={selected}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center transition-colors',
                  selected
                    ? 'border-brand-300 bg-brand-50 text-brand-800'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                )}
              >
                <Icon className="size-4" aria-hidden />
                <span className="text-[11px] leading-tight">
                  {VEHICLE_CATEGORY_LABELS[value].split(' / ')[0]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <StepHeading
            step={4}
            title="Default remittance fee"
            hint="What this kind of vehicle owes. A driver can be given their own figure, which this never overrides."
          />
          <div className="flex flex-wrap items-center justify-between gap-3 pl-7">
            <div>
              {current ? (
                <>
                  <p className="text-lg font-semibold tabular-nums text-slate-900">
                    {money(current.amount, business?.currencyCode ?? 'NGN')}
                    <span className="ml-1.5 text-sm font-normal text-slate-500">
                      {REMITTANCE_FREQUENCY_LABELS[current.frequency].toLowerCase()}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    From {current.effectiveFrom}
                    {current.stateCode ? (
                      <> · {findState(countryCode, current.stateCode)?.name ?? current.stateCode}</>
                    ) : (
                      <> · every state</>
                    )}
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No default set for {VEHICLE_CATEGORY_LABELS[category].toLowerCase()} yet.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              {(rates.data ?? []).some((r) => r.category === category) ? (
                <Button variant="ghost" size="sm" icon={<History className="size-4" />} onClick={() => setShowHistory(true)}>
                  History
                </Button>
              ) : null}
              {mayManage ? (
                <Button
                  variant={current ? 'secondary' : 'primary'}
                  size="sm"
                  icon={current ? <Pencil className="size-4" /> : undefined}
                  onClick={() => setEditingFee(true)}
                >
                  {current ? 'Change' : 'Set a default'}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      {editingFee ? (
        <FeeForm
          category={category}
          stateCode={currentState}
          current={current ?? null}
          onClose={() => setEditingFee(false)}
        />
      ) : null}

      {showHistory ? (
        <FeeHistory
          category={category}
          rates={(rates.data ?? []).filter((r) => r.category === category)}
          onClose={() => setShowHistory(false)}
        />
      ) : null}
    </div>
  );
}

/** Setting a default: an amount, how often, and from when. */
function FeeForm({
  category,
  stateCode,
  current,
  onClose,
}: {
  category: VehicleCategory;
  stateCode: string | null;
  current: VehicleRateView | null;
  onClose: () => void;
}): ReactNode {
  const { business } = useAuth();
  const save = useSetVehicleRate();

  const [amount, setAmount] = useState(current?.amount ?? '');
  const [frequency, setFrequency] = useState<RemittanceFrequency>(current?.frequency ?? 'weekly');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [perState, setPerState] = useState(Boolean(current?.stateCode ?? stateCode));
  const [note, setNote] = useState('');

  const fields = save.error instanceof ApiError ? save.error.fields : undefined;
  const states = statesOf(business?.countryCode ?? 'NG');

  return (
    <Modal
      open
      onClose={onClose}
      title={`Default remittance — ${VEHICLE_CATEGORY_LABELS[category]}`}
    >
      <form
        className="space-y-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(
            {
              category,
              frequency,
              amount,
              effectiveFrom,
              stateCode: perState ? stateCode : null,
              ...(note ? { note } : {}),
            },
            { onSuccess: onClose },
          );
        }}
      >
        {save.isError && !fields ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">
              {save.error instanceof ApiError ? save.error.message : 'Could not save the default.'}
            </p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={`Amount (${business?.currencyCode ?? 'NGN'})`}
            inputMode="decimal"
            required
            autoFocus
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            error={fields?.amount?.[0]}
          />
          <Select
            label="How often"
            value={frequency}
            onChange={(event) => setFrequency(event.target.value as RemittanceFrequency)}
            options={REMITTANCE_FREQUENCIES.map((value) => ({
              value,
              label: REMITTANCE_FREQUENCY_LABELS[value],
            }))}
          />
        </div>

        <Input
          label="Effective from"
          type="date"
          required
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
          error={fields?.effectiveFrom?.[0]}
          hint="Weeks already accrued keep the figure they charged. This governs the ones from this date on."
        />

        {states.length > 0 && stateCode ? (
          <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3">
            <input
              type="checkbox"
              checked={perState}
              onChange={(event) => setPerState(event.target.checked)}
              className="mt-0.5 size-4 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">
              Only in {findState(business?.countryCode ?? 'NG', stateCode)?.name ?? stateCode}
              <span className="block text-xs text-slate-500">
                Unticked, it applies in every state — and a state's own price still wins where one
                is set.
              </span>
            </span>
          </label>
        ) : null}

        <Input
          label="Note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          hint="Optional. Why it changed, for whoever reads this next year."
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save default
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Every default this category has had, so a past week stays explicable. */
function FeeHistory({
  category,
  rates,
  onClose,
}: {
  category: VehicleCategory;
  rates: VehicleRateView[];
  onClose: () => void;
}): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';

  return (
    <Modal open onClose={onClose} title={`${VEHICLE_CATEGORY_LABELS[category]} — fee history`}>
      <ul className="divide-y divide-slate-100">
        {rates.map((rate) => (
          <li key={rate.id} className="flex items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium tabular-nums text-slate-900">
                {money(rate.amount, currency)}
                <span className="ml-1.5 text-xs font-normal text-slate-500">
                  {REMITTANCE_FREQUENCY_LABELS[rate.frequency].toLowerCase()}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {rate.effectiveFrom} → {rate.effectiveTo ?? 'now'}
                {rate.stateCode ? ` · ${rate.stateCode}` : ' · every state'}
              </p>
              {rate.note ? <p className="mt-0.5 text-xs text-slate-400">{rate.note}</p> : null}
            </div>
            {rate.effectiveTo === null ? (
              <Badge tone="success">
                <Check className="mr-1 size-3" aria-hidden />
                In force
              </Badge>
            ) : (
              <Badge tone="neutral">Closed</Badge>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        A change closes the standing rate rather than editing it, so the amount a past week charged
        is still here to be read.
      </p>
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
